import React, { useState, useMemo, useEffect } from "react";
import "./SolarPanelsHeatmap.css";
import { Card } from "reactstrap";
import { Tooltip } from "antd";
import { gql, useQuery } from "@apollo/client";
import { error } from "../../Controllers/toast";
import Loader from "../../Loader";

const GET_PO_GROUPS = gql`
  query GetOptimizers($plant_id: Int!, $date_time: Date, $offset_date: Int) {
    GetOptimizers(plant_id: $plant_id, date_time: $date_time, offset_date: $offset_date) {
      panelsPerRow
      order_index
      optimizers {
        PO_id
        serial_number
        order_index
        Last_Production {
          Vo
          Vpv
          Ipv
          Vbat
          Ibat
          Ein
          Temp
          SOC
          date_time
        }
      }
    }
  }
`;

function SolarPanelsHeatmap({ plantId }) {
  const [optimizerGroups, setOptimizerGroups] = useState([]);
  const [date_time, setDate_time] = useState(new Date());
  const offset_date = date_time.getTimezoneOffset();
  const [heatmapMode, setHeatmapMode] = useState("Ppv");

  useEffect(() => {
    const interval = setInterval(() => setDate_time(new Date()), 5 * 60000);
    return () => clearInterval(interval);
  }, []);

  const chunkArray = (array, chunkSize) => {
    const result = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }
    return result;
  };

  const { loading } = useQuery(GET_PO_GROUPS, {
    variables: { plant_id: parseInt(plantId), date_time, offset_date },
    onCompleted: (res) => {
      const groups = res.GetOptimizers.map(po_group => {
        const sortedOptimizers = [...po_group.optimizers].sort((a, b) => {
          const A = a.order_index.split("-").map(Number);
          const B = b.order_index.split("-").map(Number);
          for (let i = 0; i < Math.max(A.length, B.length); i++) {
            if ((A[i] || 0) !== (B[i] || 0)) return (A[i] || 0) - (B[i] || 0);
          }
          return 0;
        });
        
        return {
          ...po_group,
          optimizers: sortedOptimizers,
          gridRows: chunkArray(sortedOptimizers, po_group.panelsPerRow || 5)
        };
      });
      
      setOptimizerGroups(groups);
      console.log("Optimizer Groups:", groups);
    },
    onError: () => error("مشکلی در دریافت اطلاعات رخ داده است!"),
    pollInterval: 20000,
  });

  const checkMinusValue = (val) => (val > 0 ? val : 0);

  const allOptimizers = useMemo(() => 
    optimizerGroups.flatMap(group => group.optimizers), 
    [optimizerGroups]
  );

  const maxValues = useMemo(() => ({
    Ppv: (() => {
      const maxVal = Math.max(...allOptimizers.map(p =>
        Math.max((p.Last_Production?.[0]?.Vpv * p.Last_Production?.[0]?.Ipv) / 100000 || 0, 0)
      ));
      return maxVal > 800 ? maxVal : 800;
    })(),
    Pbat: (() => {
      const maxVal = Math.max(...allOptimizers.map(p =>
        (p.Last_Production?.[0]?.Vbat * p.Last_Production?.[0]?.Ibat) / 100000 || 0
      ));
      return maxVal > 600 ? maxVal : 600;
    })(),
    SOC: 100,
    Temp: 100
  }), [allOptimizers]);

  const minValues = useMemo(() => ({
    Ppv: 0,
    Pbat: (() => {
      const minVal = Math.min(...allOptimizers.map(p =>
        (p.Last_Production?.[0]?.Vbat * p.Last_Production?.[0]?.Ibat) / 100000 || 0
      ));
      return minVal < -600 ? minVal : -600;
    })(),
    SOC: 0,
    Temp: -20
  }), [allOptimizers]);

  const avgValues = useMemo(() => {
    const types = ["Ppv", "Pbat", "SOC", "Temp"];
    const sums = { Ppv: 0, Pbat: 0, SOC: 0, Temp: 0 };
    const counts = { Ppv: 0, Pbat: 0, SOC: 0, Temp: 0 };

    allOptimizers.forEach(p => {
      const L = p.Last_Production?.[0] || {};
      const Ppv = Math.max((L.Vpv * L.Ipv) / 100000 || 0, 0);
      const Pbat = (L.Vbat * L.Ibat) / 100000 || 0;
      const SOC = L.SOC / 100 || 0;
      const Temp = L.Temp / 100 || 0;
      if (Ppv > 0) { sums.Ppv += Ppv; counts.Ppv++; }
      sums.Pbat += Pbat; counts.Pbat++;
      sums.SOC += SOC; counts.SOC++;
      sums.Temp += Temp; counts.Temp++;
    });

    const avg = {};
    types.forEach(t => { avg[t] = counts[t] ? sums[t] / counts[t] : 0; });
    return avg;
  }, [allOptimizers]);

  const getColorByValue = (val, type) => {
    if (val === null || val === undefined || isNaN(val)) return "gray";

    if (type === "Temp") {
      const min = -20;
      const max = 100;
      const v = Math.max(min, Math.min(max, val));

      if (v <= 0) {
        const t = (v - min) / (0 - min);
        const r = 0;
        const g = Math.round(255 * t);
        const b = Math.round(255 - 255 * t);
        return `rgb(${r},${g},${b})`;
      } else if (v <= 40) {
        const t = (v - 0) / 40;
        const r = Math.round(255 * t);
        const g = 255;
        const b = 0;
        return `rgb(${r},${g},${b})`;
      } else {
        const t = (v - 40) / (max - 40);
        const r = 255;
        const g = Math.round(255 - 255 * t);
        const b = 0;
        return `rgb(${r},${g},${b})`;
      }
    }

    if (type === "SOC") {
      const t = Math.max(0, Math.min(1, val / 100));
      const r = Math.round(255 * (1 - t));
      const g = Math.round(255 * t);
      const b = 0;
      return `rgb(${r},${g},${b})`;
    }

    if (type === "Pbat") {
      const min = -100;
      const max = 100;
      const v = Math.max(min, Math.min(max, val));
      const mid = 0;

      if (v < mid) {
        const t = (v - min) / (mid - min);
        const r = Math.round(0 + t * 150);
        const g = Math.round(150 + t * 50);
        const b = Math.round(255 - t * 55);
        return `rgb(${r},${g},${b})`;
      } else {
        const t = (v - mid) / (max - mid);
        const r = Math.round(200 + t * 55);
        const g = Math.round(200 - t * 200);
        const b = Math.round(200 - t * 200);
        return `rgb(${r},${g},${b})`;
      }
    }

    const t = Math.max(0, Math.min(1, val / maxValues.Ppv));
    const r = Math.round(248 + t * 7);
    const g = Math.round(255 - t * 102);
    const b = Math.round(156 - t * 156);
    return `rgb(${r},${g},${b})`;
  };

  const generateGradient = (type) => {
    const steps = 10;
    let gradient = "linear-gradient(to right, ";

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      let value;

      switch (type) {
        case "Temp":
          value = minValues.Temp + t * (maxValues.Temp - minValues.Temp);
          break;
        case "SOC":
          value = minValues.SOC + t * (maxValues.SOC - minValues.SOC);
          break;
        case "Pbat":
          value = minValues.Pbat + t * (maxValues.Pbat - minValues.Pbat);
          break;
        case "Ppv":
        default:
          value = minValues.Ppv + t * (maxValues.Ppv - minValues.Ppv);
          break;
      }

      const color = getColorByValue(value, type);
      gradient += `${color} ${t * 100}%`;
      if (i < steps) gradient += ", ";
    }

    gradient += ")";
    return gradient;
  };

  const heatmapOptions = [
    { key: "Ppv", label: "توان پنل", color: "#3b82f6", unit: "W" },
    { key: "Pbat", label: "توان باتری", color: "#3b82f6", unit: "W" },
    { key: "SOC", label: "شارژ باتری", color: "#3b82f6", unit: "%" },
    { key: "Temp", label: "دمای دستگاه", color: "#3b82f6", unit: "°C" }
  ];

  const activeOption = heatmapOptions.find(opt => opt.key === heatmapMode);
  const avgNormalized = ((avgValues[heatmapMode] - minValues[heatmapMode]) / (maxValues[heatmapMode] - minValues[heatmapMode] || 1)) * 100;

  const totalOptimizers = allOptimizers.length;

  return (
    <Card className="border-radius-15">
      {loading ? <Loader /> :
        <div className="heatmap-container">
          <div className="heatmap-header">
            <div className="header-left">
              <svg className="sun-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="5" strokeWidth="2" />
                <line x1="12" y1="1" x2="12" y2="3" strokeWidth="2" />
                <line x1="12" y1="21" x2="12" y2="23" strokeWidth="2" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" strokeWidth="2" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" strokeWidth="2" />
                <line x1="1" y1="12" x2="3" y2="12" strokeWidth="2" />
                <line x1="21" y1="12" x2="23" y2="12" strokeWidth="2" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" strokeWidth="2" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" strokeWidth="2" />
              </svg>
              <p className="heatmap-title">نقشه توزیعی دستگاه‌ها</p>
            </div>
            <div className="active-badge">{totalOptimizers} دستگاه</div>
          </div>

          <div className="heatmap-badges" style={{ display: 'flex', gap: '10px', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {heatmapOptions.map(opt => (
              <div
                key={opt.key}
                onClick={() => setHeatmapMode(opt.key)}
                style={{
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "10px",
                  fontWeight: 600,
                  letterSpacing: "0.3px",
                  backdropFilter: "blur(10px)",
                  background: heatmapMode === opt.key ? `linear-gradient(135deg, ${opt.color}AA, ${opt.color}55)` : "rgba(255,255,255,0.15)",
                  border: heatmapMode === opt.key ? `1px solid ${opt.color}` : "1px solid rgba(255,255,255,0.3)",
                  color: heatmapMode === opt.key ? "white" : "#555",
                  boxShadow: heatmapMode === opt.key ? `0 0 12px ${opt.color}88` : "0 2px 8px rgba(0,0,0,0.15)",
                  transition: "all 0.3s ease",
                  userSelect: "none"
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px", position: "relative" }}>
            <span style={{ fontSize: "0.9rem", color: "#555", direction: "ltr" }}>{maxValues[heatmapMode]?.toFixed(0)} {activeOption?.unit}</span>
            <div
              style={{
                flex: 1,
                height: "14px",
                borderRadius: "10px",
                background: generateGradient(heatmapMode),
                boxShadow: `0 0 8px ${activeOption?.color}77`,
                position: "relative"
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: `${avgNormalized}%`,
                  top: "-3px",
                  width: "10px",
                  height: "20px",
                  borderRadius: "50%",
                  background: "black",
                  transform: "translateX(-50%)",
                  boxShadow: "0 0 4px rgba(0,0,0,0.5)"
                }}
              />
            </div>
            <span style={{ fontSize: "0.9rem", color: "#555", direction: "ltr" }}>{minValues[heatmapMode]?.toFixed(0)} {activeOption?.unit}</span>
          </div>

          <div className="heatmap-groups-container">
            {optimizerGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="optimizer-group">
                <div className="group-header">
                  <h3>گروه <span style={{direction:"ltr"}}>{group.order_index}</span></h3>
                  <span className="group-badge">{group.optimizers.length} پنل</span>
                </div>
                
                <div 
                  className="group-panels-grid"
                  style={{
                    gridTemplateColumns: `repeat(${group.panelsPerRow || 5}, 1fr)`
                  }}
                >
                  {group.optimizers.map((optimizer) => {
                    const production = optimizer.Last_Production?.[0] || {};
                    const { Vpv = 0, Ipv = 0, Vbat = 0, Ibat = 0, Temp = 0, SOC = 0 } = production;

                    let valueForColor;
                    switch (heatmapMode) {
                      case "Ppv": valueForColor = Math.round((Vpv * Ipv) / 100000); break;
                      case "Pbat": valueForColor = Math.round((Vbat * Ibat) / 100000); break;
                      case "SOC": valueForColor = SOC / 100; break;
                      case "Temp": valueForColor = Temp / 100; break;
                      default: valueForColor = Math.max((Vpv * Ipv) / 100000, 0);
                    }

                    const socPercent = Math.min(Math.max(SOC / 100, 0), 100);

                    return (
                      <Tooltip
                        key={optimizer.PO_id}
                        title={
                          <div>
                            <div className="tooltip-header">
                              <span className="tooltip-serial">{optimizer.serial_number}</span>
                              <span className="tooltip-order">{optimizer.order_index}</span>
                            </div>
                            <div className="tooltip-content">
                              <div className="tooltip-row"><span>توان تولیدی:</span><strong dir="ltr">{checkMinusValue(Vpv * Ipv / 100000).toFixed(0)} W</strong></div>
                              <div className="tooltip-row"><span>توان باتری:</span><strong dir="ltr">{(Vbat * Ibat / 100000).toFixed(0)} W</strong></div>
                              <div className="tooltip-row"><span>شارژ باتری:</span><strong>{checkMinusValue(socPercent).toFixed(0)}%</strong></div>
                              <div className="tooltip-row"><span>دمای دستگاه:</span><strong dir="ltr">{(Temp / 100).toFixed(0)}°C</strong></div>
                            </div>
                            <div className="battery-bar rtl">
                              <div className="battery-fill rtl" style={{ width: `${socPercent}%` }} />
                            </div>
                          </div>
                        }
                      >
                        <div className="panel-wrapper">
                          <div
                            className="panel-dot"
                            style={{
                              backgroundColor: getColorByValue(valueForColor, heatmapMode),
                              boxShadow: valueForColor !== 0 ? `0 0 ${8 + (Math.abs(valueForColor) / (maxValues[heatmapMode])) * 12}px ${getColorByValue(valueForColor, heatmapMode)}` : "none",
                            }}
                          />
                          <div className="panel-label">
                            {optimizer.order_index}
                          </div>
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      }
    </Card>
  )
}

export default React.memo(SolarPanelsHeatmap);